import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useDebouncedCallback } from 'use-debounce'
import {
  type BasePuiDialogProps,
  Defaults,
  Utils,
} from '@pbt/pbt-ui-components'

import {
  type MedicalHistoryConfig,
  useGeneratedMedicalHistoryConfigLazyQuery,
} from '~/api/graphql/generated/types'
import EmailDialogWithSections, {
  SuccessDialogKind,
} from '~/components/common/dialog/email/EmailDialogWithSections'
import { GeneratingPdfContentKind } from '~/constants/communications'
import FeatureToggle from '~/constants/featureToggle'
import { emailMedHistory } from '~/store/actions/communications'
import { fetchMedicalHistoryGeneratedMessagePreview } from '~/store/actions/medicalHistory'
import {
  clearMedicalHistoryCardConfig,
  clearMedicalHistoryCardPreviewTemplate,
  fetchMedicalHistoryCardConfig,
  fetchMedicalHistoryCardPreviewTemplate,
  getMedicalHistoryCardConfig,
  getMedicalHistoryCardConfigIsLoading,
  getMedicalHistoryCardIsSendingEmail,
  getMedicalHistoryCardPreviewTemplate,
  getMedicalHistoryCardTemplateIsReceiving,
} from '~/store/duck/medicalHistoryCard'
import {
  getFeatureToggle,
  getIsFilterUnfinalizedSoapFromMedicalRecordsEnabled,
} from '~/store/reducers/constants'
import { getPatient } from '~/store/reducers/patients'
import { getUser } from '~/store/reducers/users'
import type { EmailEntityConfig, MedicalHistoryCardConfig } from '~/types'
import {
  convertGraphqlToMedicalHistoryConfig,
  convertMedicalHistoryConfigToPreviewInput,
  mergeMedicalHistoryConfigs,
  normalizeSections,
  useQueryAttachmentsSections,
} from '~/utils/reportCardUtils'

interface MedicalHistoryCardDialogProps extends BasePuiDialogProps {
  clientId: string
  patientId: string
}

const MedicalHistoryCardDialog = ({
  open,
  onClose,
  patientId,
  clientId,
}: MedicalHistoryCardDialogProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['Common', 'Dialogs'])

  const config = useSelector(getMedicalHistoryCardConfig)
  const configIsLoading = useSelector(getMedicalHistoryCardConfigIsLoading)
  const previewTemplate = useSelector(getMedicalHistoryCardPreviewTemplate)
  const templateIsReceiving = useSelector(
    getMedicalHistoryCardTemplateIsReceiving,
  )
  const client = useSelector(getUser(clientId))
  const patient = useSelector(getPatient(patientId))
  const isFilterUnfinalizedSoapFromMedicalRecordsEnabled = useSelector(
    getIsFilterUnfinalizedSoapFromMedicalRecordsEnabled,
  )
  const isMedicalHistoryLabAttachmentsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.MEDICAL_HISTORY_LAB_ATTACHMENTS),
  )
  const isMedicalHistoryImagingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.MEDICAL_RECORD_DIAGNOSTIC_IMAGERY_PDF),
  )
  const isMarkMessagesAsMedical = useSelector(
    getFeatureToggle(FeatureToggle.MARK_MESSAGES_AS_MEDICAL),
  )

  const [
    queryMedicalHistoryCardConfig,
    { data, loading: configQueryIsLoading },
  ] = useGeneratedMedicalHistoryConfigLazyQuery({
    variables: {
      input: {
        medicalHistory: {
          patientId,
        },
      },
    },
  })

  const {
    attachmentsData,
    labAttachmentsData,
    imagingAttachmentsData,
    hasAnyAttachmentsError,
    getHasAttachmentsErrorForSection,
    isLoadingAttachmentsSections,
    fetchAllAttachmentsSections,
  } = useQueryAttachmentsSections()

  const queryConfig = useMemo(() => {
    const graphqlConfig = convertGraphqlToMedicalHistoryConfig(
      data?.generatedMessageConfig as MedicalHistoryConfig,
      isMarkMessagesAsMedical,
    )

    if (!isMedicalHistoryImagingEnabled) {
      return graphqlConfig
    }

    if (hasAnyAttachmentsError) {
      return {
        ...graphqlConfig,
        sections: graphqlConfig?.sections?.map((section) => {
          const hasError = getHasAttachmentsErrorForSection(section?.section)
          return {
            ...section,
            defaultExpanded: true,
            hasError,
          }
        }),
      } as EmailEntityConfig
    }

    // When feature toggle is enabled, merge attachments sections (attachments, labs and imaging) into the config as we are splitting them out
    const convertedConfig = mergeMedicalHistoryConfigs({
      config: graphqlConfig,
      sections: isMedicalHistoryImagingEnabled
        ? {
            attachments: attachmentsData,
            labs: labAttachmentsData,
            imaging: imagingAttachmentsData,
          }
        : undefined,
    })

    return convertedConfig
  }, [
    data?.generatedMessageConfig,
    attachmentsData,
    labAttachmentsData,
    imagingAttachmentsData,
    isMedicalHistoryImagingEnabled,
    isMarkMessagesAsMedical,
    hasAnyAttachmentsError,
    getHasAttachmentsErrorForSection,
  ])

  useEffect(() => {
    if (!open) {
      return
    }

    const fetchData = async () => {
      if (isMedicalHistoryLabAttachmentsEnabled) {
        await queryMedicalHistoryCardConfig()
        if (isMedicalHistoryImagingEnabled) {
          await fetchAllAttachmentsSections({ patientId })
        }
      } else {
        dispatch(fetchMedicalHistoryCardConfig(patientId))
      }
    }

    fetchData()
  }, [
    open,
    isMedicalHistoryLabAttachmentsEnabled,
    isMedicalHistoryImagingEnabled,
  ])

  const handleClose = () => {
    dispatch(clearMedicalHistoryCardPreviewTemplate())
    dispatch(clearMedicalHistoryCardConfig())
    if (onClose) {
      onClose()
    }
  }

  const fetchPreviewTemplate = (
    historyCardConfig: MedicalHistoryCardConfig,
  ) => {
    if (open && historyCardConfig?.sections) {
      if (isFilterUnfinalizedSoapFromMedicalRecordsEnabled) {
        const medicalHistoryPreview = convertMedicalHistoryConfigToPreviewInput(
          historyCardConfig,
          patientId,
        )
        dispatch(
          fetchMedicalHistoryGeneratedMessagePreview({
            ...medicalHistoryPreview,
            finalizedSoapOnly: true,
          }),
        )
      } else {
        dispatch(
          fetchMedicalHistoryCardPreviewTemplate(historyCardConfig, patientId),
        )
      }
    }
  }

  const handleDateRangeChange = async (
    historyCardConfig: MedicalHistoryCardConfig,
  ) => {
    const { from, to } = historyCardConfig

    if (isMedicalHistoryLabAttachmentsEnabled) {
      await queryMedicalHistoryCardConfig({
        variables: { input: { medicalHistory: { patientId, from, to } } },
      })
      if (isMedicalHistoryImagingEnabled) {
        await fetchAllAttachmentsSections({ patientId, from, to })
      }
    } else {
      dispatch(fetchMedicalHistoryCardConfig(patientId, from, to))
    }
  }

  const fetchPreviewTemplateDebounced = useDebouncedCallback(
    fetchPreviewTemplate,
    Defaults.DEBOUNCE_ACTION_TIME,
  )

  const handleSendEmail = (entityConfig: EmailEntityConfig) => {
    if (isMedicalHistoryLabAttachmentsEnabled) {
      const normalizedEntityConfig = {
        ...entityConfig,
        sections: normalizeSections(entityConfig.sections),
      } as EmailEntityConfig
      dispatch(
        emailMedHistory({
          patientId,
          ...normalizedEntityConfig,
          ...(isFilterUnfinalizedSoapFromMedicalRecordsEnabled
            ? { finalizedSoapOnly: true }
            : {}),
        }),
      )
    } else {
      dispatch(
        emailMedHistory({
          patientId,
          ...entityConfig,
          ...(isFilterUnfinalizedSoapFromMedicalRecordsEnabled
            ? { finalizedSoapOnly: true }
            : {}),
        }),
      )
    }
  }

  const handlePrint = (historyCardConfig: MedicalHistoryCardConfig) => {
    if (isFilterUnfinalizedSoapFromMedicalRecordsEnabled) {
      const medicalHistoryPreview = convertMedicalHistoryConfigToPreviewInput(
        historyCardConfig,
        patientId,
      )
      dispatch(
        fetchMedicalHistoryGeneratedMessagePreview({
          ...medicalHistoryPreview,
          finalizedSoapOnly: true,
        }),
      )
    } else {
      dispatch(
        fetchMedicalHistoryCardPreviewTemplate(historyCardConfig, patientId),
      )
    }
  }

  const pdfFileConfig = {
    patientId,
    ...(isFilterUnfinalizedSoapFromMedicalRecordsEnabled
      ? { finalizedSoapOnly: true }
      : {}),
  }
  const defaultSubject = t(
    'Dialogs:MEDICAL_HISTORY_CARD_DIALOG.DEFAULT_SUBJECT',
    {
      patientName: patient?.name || '',
    },
  )
  const popupEmail = ({ mailTo }: EmailEntityConfig) =>
    t('Dialogs:MEDICAL_HISTORY_CARD_DIALOG.EMAIL_SENT_MESSAGE', {
      patientName: patient?.name || t('Common:PATIENT'),
      clientName: Utils.getPersonString(client),
      mailTo: mailTo || client?.email,
    })

  return (
    <>
      <EmailDialogWithSections
        cardEntityName={t(
          'Dialogs:MEDICAL_HISTORY_CARD_DIALOG.TITLE',
        ).toLowerCase()}
        clientId={clientId}
        config={isMedicalHistoryLabAttachmentsEnabled ? queryConfig : config}
        configIsLoading={
          isMedicalHistoryLabAttachmentsEnabled
            ? configQueryIsLoading || isLoadingAttachmentsSections
            : configIsLoading
        }
        defaultDateRangeLabel={t('Common:ENTIRE_HISTORY')}
        defaultSubject={defaultSubject}
        dialogHeader={t('Dialogs:MEDICAL_HISTORY_CARD_DIALOG.HEADER')}
        emailTemplate={previewTemplate}
        isSendingEmailSelector={getMedicalHistoryCardIsSendingEmail}
        open={open}
        patient={patient}
        pdfContentKind={GeneratingPdfContentKind.MEDICAL_HISTORY}
        pdfFileConfig={pdfFileConfig}
        popupEmailSentMessageHandler={popupEmail}
        printTemplate={previewTemplate}
        successDialogKind={SuccessDialogKind.POPUP}
        templateIsReceiving={templateIsReceiving}
        onChange={fetchPreviewTemplateDebounced}
        onClearPrint={clearMedicalHistoryCardPreviewTemplate}
        onClose={handleClose}
        onMedicalHistoryDateRangeChange={handleDateRangeChange}
        onPrint={handlePrint}
        onSendEmail={handleSendEmail}
      />
    </>
  )
}

export default MedicalHistoryCardDialog
